import { defineComponent as _defineComponent } from 'vue'
import type { Package } from '@/@types/graphql-types';
import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import type {
	CreateTextRecommendationAndProfileMutation,
	CreateTextRecommendationAndProfileMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/CreateTextRecommendationAndProfile.mutation';
import type {
	UpdateTextRecommendationMutation,
	UpdateTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/UpdateTextRecommendation.mutation';

import { CreateTextRecommendationAndProfileDocument } from '@/features/why-to-watch/graphql/mutations/CreateTextRecommendationAndProfile.mutation';
import { UpdateTextRecommendationDocument } from '@/features/why-to-watch/graphql/mutations/UpdateTextRecommendation.mutation';

import { MonetizationType } from '@/@types/graphql-types';

import BaseModalNew from '@/components/modals/BaseModalNew.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import InputAvatar from '@/features/ui/components/InputAvatar.vue';
import InputText from '@/features/ui/components/InputText.vue';
import InputTextArea from '@/features/ui/components/InputTextArea.vue';
import PackageRow from '@/components/modals/package-selection/PackageRow.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import SearchBar from '@/features/search/components/SearchBar.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { isMobile } from '@/helpers/ui-helper';
import { toastSuccess, toastError } from '@/helpers/vfm/toast-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { useMutation } from '@/helpers/composables/useApollo';
import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';

import { computed, ref } from 'vue';
import {
	CreateTextRecommendationDocument,
	CreateTextRecommendationMutation,
	CreateTextRecommendationMutationVariables,
} from '../graphql/mutations/CreateTextRecommendation.mutation';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useWtwTracking } from '../tracking';


export default /*#__PURE__*/_defineComponent({
  __name: 'TextRecommendationModal',
  props: {
    title: null,
    formData: null,
    editMode: { type: Boolean, default: false }
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'close'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, formData?: Partial<TextRecommendationFragment> | null, editMode: boolean };





const userStore = useUserStore();
const { country, language } = useLanguageStore();
const { tc } = useI18n();

const providerSearch = ref<string>('');
const displayProviderSearch = ref(false);
const isSubmitting = ref(false);

// Form Data
const id = ref<string>(props.formData?.id ?? '');
const headline = ref<string>(props.formData?.headline ?? '');
const body = ref<string>(props.formData?.body ?? '');
const watchedOn = ref(props.formData?.watchedOn ?? null);

const fullName = ref<string>(userStore.userInfo.value.name ?? '');
const avatarBase64 = ref<string | null>(userStore.userInfo.value.photoURL ?? null);

function handleAvatarBase64(base64: string) {
	trackWtw('imageSelected');
	avatarBase64.value = base64;
}

const titleName = computed(() =>
	props.title.__typename === 'Season' && !!props.title.show
		? `${props.title.show.content.title} - ${props.title.content.title}`
		: props.title.content.title
);

const uniqueProviders = computed(() => {
	const uniqueProviderMap = new Map();

	props.title.offers
		.filter(offer => offer.monetizationType !== MonetizationType.Cinema)
		.forEach(offer => {
			if (!uniqueProviderMap.has(offer.package.shortName)) {
				uniqueProviderMap.set(offer.package.shortName, offer.package);
			}
		});

	const uniqueProviders = Array.from(uniqueProviderMap.values());

	// Add cinema provider to end of list if movie
	if (props.title.__typename === 'Movie') {
		uniqueProviders.push({
			providerShortName: undefined,
			clearName: tc('WEBAPP_CINEMA'),
		});
	}

	return uniqueProviders;
});

const providers = computed(() =>
	uniqueProviders.value.filter(p => p.clearName.toUpperCase().includes(providerSearch.value.toUpperCase()))
);

const selectedProviderImageUrl = computed(() => {
	if (watchedOn.value?.packageId === 0) {
		return 'https://static.justwatch.com/static/compile_jw/assets/jw-icon-badge.png';
	}

	return watchedOn.value?.icon
		? `${JW_CONFIG.IMAGESCALER_URL}${watchedOn.value?.icon.replace('{profile}', 's40')}`
		: '';
});

const hasProfile = computed(() => {
	const profileUUID = userStore.profileInfo.value?.uuid;

	return profileUUID != null && profileUUID !== '';
});

const submitValidation = computed(() => {
	const wtwValid = headline.value && body.value && body.value.length >= 60;
	const profileValid = fullName.value.trim().length > 2;

	if (props.editMode) return wtwValid;

	return hasProfile.value ? wtwValid : profileValid && wtwValid;
});

function toggleProviderSearch() {
	if (displayProviderSearch.value === false) {
		trackWtw('providerDropdownClicked');
	}
	displayProviderSearch.value = !displayProviderSearch.value;
}

function providerClickHandler(provider: Package) {
	trackWtw('providerSelected');

	watchedOn.value = provider;
	toggleProviderSearch();
}

const createRecommendationAndProfile = useMutation<
	CreateTextRecommendationAndProfileMutation,
	CreateTextRecommendationAndProfileMutationVariables
>(CreateTextRecommendationAndProfileDocument, {
	refetchQueries: ['GetTextRecommendations'],
});

const createRecommendation = useMutation<CreateTextRecommendationMutation, CreateTextRecommendationMutationVariables>(
	CreateTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

const updateRecommendation = useMutation<UpdateTextRecommendationMutation, UpdateTextRecommendationMutationVariables>(
	UpdateTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

async function handleSubmit() {
	if (!submitValidation.value || isSubmitting.value) {
		trackWtwDisabledClick();
		return;
	}

	isSubmitting.value = true;

	if (props.editMode) {
		await updateRecommendation.mutate({
			input: {
				id: id.value,
				headline: headline.value,
				body: body.value,
				watchedOn: watchedOn.value?.packageId ?? 0,
			},
			country: country.value,
			language: language.value,
		});
	} else {
		if (hasProfile.value) {
			await createRecommendation.mutate({
				input: {
					textRecommendation: {
						headline: headline.value,
						body: body.value,
						watchedOn: watchedOn.value?.packageId ?? 0,
					},
					jwEntityID: props.title.id,
					country: country.value,
				},
				country: country.value,
				language: language.value,
			});
		} else {
			await createRecommendationAndProfile.mutate({
				input: {
					name: fullName.value,
					avatarBase64: avatarBase64.value,
					textRecommendation: {
						headline: headline.value,
						body: body.value,
						watchedOn: watchedOn.value?.packageId ?? 0,
					},
					jwEntityID: props.title.id,
					country: country.value,
				},
				country: country.value,
				language: language.value,
			});
		}
	}

	isSubmitting.value = false;

	trackWtw('addWhyToWatch');
	emit('close');
}

function close() {
	trackWtw('modalClosed');
	emit('close');
}

updateRecommendation.onDone(() => onSuccess(tc('WEBAPP_RECOMMENDATION_UPDATED_SUCCESS')));
updateRecommendation.onError(error => {
	console.error(error);
	onError(tc('WEBAPP_RECOMMENDATION_UPDATED_ERROR'));
});

createRecommendation.onDone(() => onSuccess(tc('WEBAPP_RECOMMENDATION_ADDED_SUCCESS')));
createRecommendation.onError(error => {
	console.error(error);
	onError(tc('WEBAPP_RECOMMENDATION_ADDED_ERROR'));
});

createRecommendationAndProfile.onDone(() => onSuccess(tc('WEBAPP_RECOMMENDATION_ADDED_SUCCESS')));
createRecommendationAndProfile.onError(error => {
	console.error(error);
	onError(tc('WEBAPP_RECOMMENDATION_ADDED_ERROR'));
});

function onSuccess(message: string) {
	toastSuccess({ message, duration: 3000, position: isMobile() ? 'top' : 'bottom' });
}

function onError(message: string) {
	toastError({ message, duration: 3000, position: isMobile() ? 'top' : 'bottom' });
}

// TRACKING
const { trackWtw, trackWtwDisabledClick } = useWtwTracking({ title: () => props.title });

return { __sfc: true,props, emit, userStore, country, language, tc, providerSearch, displayProviderSearch, isSubmitting, id, headline, body, watchedOn, fullName, avatarBase64, handleAvatarBase64, titleName, uniqueProviders, providers, selectedProviderImageUrl, hasProfile, submitValidation, toggleProviderSearch, providerClickHandler, createRecommendationAndProfile, createRecommendation, updateRecommendation, handleSubmit, close, onSuccess, onError, trackWtw, trackWtwDisabledClick, BaseModalNew, BasicButton, InputAvatar, InputText, InputTextArea, PackageRow, PictureComp, SearchBar, FontAwesomeIcon, faChevronUp, faChevronDown }
}

})